import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocalisation } from '@archipro-website/localisation';

import type { EnquirerType, EnquiryProjectKind } from 'generated/graphql';
import type { PublicEnquiryForm } from '~/modules/enquiry/types/public-enquiry-form';
import {
    NO_CONFIGURED_ERRORS,
    getConfiguredEnquiryRules,
    getRoutingCountry,
    hasConfiguredErrors,
    predictConfiguredDestination,
    validateConfiguredAnswers,
    type ConfiguredEnquiryErrors,
    type ConfiguredEnquiryRules,
} from '~/modules/enquiry/domain/configured-enquiry';
import {
    preselectBranchId,
    resolveBranchPicker,
    type ResolvedBranchPicker,
} from '~/modules/enquiry/domain/branch-picker';
import type { PredictedDestination } from '~/modules/enquiry/util/predict-enquiry-destination';
import {
    enquirySessionAtom,
    parseSuburbFromProfile,
    stripPostcodeNulls,
} from '~/modules/enquiry/hooks/use-enquiry';
import { useAtomValue } from 'jotai';
import { useUserLoggedIn } from '@modules/user';
import {
    EMPTY_BOOKING_ANSWERS,
    type BookingFormAnswers,
    type BookingLocationAnswer,
} from '../domain/book-meeting-flow';

const BRANCH_REQUIRED_ERROR = 'Please choose a branch.';
const LOCATION_REQUIRED_ERROR = 'Please enter your suburb or postcode.';

export interface BookingFormAnswerErrors extends ConfiguredEnquiryErrors {
    branch: string | null;
    location: string | null;
}

const NO_ERRORS: BookingFormAnswerErrors = {
    ...NO_CONFIGURED_ERRORS,
    branch: null,
    location: null,
};

/** A branch select value as the id routing compares, or null when unset. */
export const toBranchId = (value: string | undefined): number | null => {
    const id = Number(value);
    return Number.isInteger(id) && id > 0 ? id : null;
};

export interface BookingFormAnswersModel {
    form: PublicEnquiryForm | null;
    rules: ConfiguredEnquiryRules | null;
    answers: BookingFormAnswers;
    errors: BookingFormAnswerErrors;
    /** The form's branch question, only when it offers a real choice. */
    branchPicker: ResolvedBranchPicker | null;
    /** ISO alpha-2 code of where the visitor appears to be. */
    visitorCountry: string | null;
    /** Where the answers route, as far as the client can tell. Null without a form. */
    predicted: PredictedDestination | null;
    setEnquiryTypeId: (typeId: string) => void;
    setEnquirerType: (value: EnquirerType) => void;
    setProjectKind: (value: EnquiryProjectKind) => void;
    setFieldAnswer: (key: string, value: string) => void;
    setBranchId: (branchId: string) => void;
    setLocation: (location: BookingLocationAnswer | null) => void;
    setMessage: (message: string) => void;
    /** Shows every error at once; true when the answers can be sent. */
    validate: () => boolean;
}

/**
 * A booking form's answers, held by the caller so they can be persisted with
 * the gate, and everything the form decides about them: which questions show,
 * which are required, and where the answers route.
 *
 * Built on the same pure rules the enquiry gate's configured-enquiry model
 * uses, so a booking and an enquiry sent from one form validate and route the
 * same way.
 */
export function useBookingFormAnswers(
    form: PublicEnquiryForm | null,
    {
        answers: storedAnswers,
        onChange,
        ipRegion = null,
    }: {
        answers: BookingFormAnswers | null;
        onChange: (next: BookingFormAnswers) => void;
        /** Where the connection geolocates, for branch preselection. */
        ipRegion?: string | null;
    },
): BookingFormAnswersModel {
    const answers = storedAnswers ?? EMPTY_BOOKING_ANSWERS;
    const rules = useMemo(
        () => (form ? getConfiguredEnquiryRules(form) : null),
        [form],
    );
    const { country } = useLocalisation();
    const visitorCountry = country ?? null;
    const [errors, setErrors] = useState<BookingFormAnswerErrors>(NO_ERRORS);

    /** Only where there is a decision: a single branch is chosen for them. */
    const resolvedPicker = useMemo(
        () =>
            form
                ? resolveBranchPicker(form, {
                      includeBranch: false,
                      branchOptions: [],
                  })
                : null,
        [form],
    );
    const onlyBranchId =
        resolvedPicker?.branchOptions.length === 1
            ? String(resolvedPicker.branchOptions[0]?.value.branchId ?? '')
            : '';
    const ipBranchId = useMemo(
        () =>
            resolvedPicker
                ? preselectBranchId(resolvedPicker, { ipRegion })
                : null,
        [resolvedPicker, ipRegion],
    );
    const branchId =
        answers.branchId ||
        onlyBranchId ||
        (ipBranchId !== null ? String(ipBranchId) : '');
    const effective = useMemo(
        () => ({ ...answers, branchId }),
        [answers, branchId],
    );

    const update = useCallback(
        (patch: Partial<BookingFormAnswers>) =>
            onChange({ ...effective, ...patch }),
        [onChange, effective],
    );

    /** The branch the location answer last picked, which a later location may replace. */
    const locationBranchRef = useRef<string | null>(null);

    /**
     * A location answer, and the branch it picks while the reader has not
     * chosen one: a new place picks again, and one with no match clears the
     * last place's pick. A branch the reader chose is never touched.
     */
    const locationPatch = (
        location: BookingLocationAnswer | null,
    ): Partial<BookingFormAnswers> => {
        const current = answers.branchId;
        const currentIsLocationPick =
            current !== '' && current === locationBranchRef.current;
        if (!resolvedPicker || (current && !currentIsLocationPick)) {
            return { location };
        }
        const preselected = location
            ? preselectBranchId(resolvedPicker, {
                  regionName: location.value.region ?? null,
              })
            : null;
        if (preselected !== null) {
            locationBranchRef.current = String(preselected);
            return { location, branchId: String(preselected) };
        }
        if (currentIsLocationPick) {
            locationBranchRef.current = null;
            return { location, branchId: '' };
        }
        return { location };
    };

    // Where they said they are last time (an earlier enquiry, or their profile) fills the
    // location question once, the way the enquiry form does. They can still change it.
    const { isLoggedIn, user } = useUserLoggedIn();
    const enquirySession = useAtomValue(enquirySessionAtom);
    const savedLocation = useMemo<BookingLocationAnswer | null>(
        () =>
            stripPostcodeNulls(enquirySession?.postcode) ??
            (isLoggedIn ? parseSuburbFromProfile(user?.Suburb) : undefined) ??
            null,
        [enquirySession?.postcode, isLoggedIn, user],
    );
    // Only fresh answers are filled: once they exist, a cleared location stays cleared.
    const isFresh = storedAnswers === null;
    useEffect(() => {
        if (!isFresh || !rules?.collectLocation || !savedLocation) return;
        update(locationPatch(savedLocation));
    });

    const clearError = (key: keyof BookingFormAnswerErrors) =>
        setErrors((current) =>
            current[key] ? { ...current, [key]: null } : current,
        );

    const predicted = useMemo(() => {
        if (!form || !rules) return null;
        return predictConfiguredDestination(form, rules, effective, {
            country: getRoutingCountry(
                rules.customFields,
                effective.fieldAnswers,
                visitorCountry,
            ),
            regionName: effective.location?.value.region ?? null,
            branchId: toBranchId(effective.branchId),
        });
    }, [form, rules, effective, visitorCountry]);

    const validate = useCallback(() => {
        if (!rules) return true;
        const configured = validateConfiguredAnswers(rules, effective);
        const next: BookingFormAnswerErrors = {
            ...configured,
            branch:
                resolvedPicker?.required && !toBranchId(effective.branchId)
                    ? BRANCH_REQUIRED_ERROR
                    : null,
            location:
                rules.collectLocation && !effective.location
                    ? LOCATION_REQUIRED_ERROR
                    : null,
        };
        setErrors(next);
        return !hasConfiguredErrors(next) && !next.branch && !next.location;
    }, [rules, effective, resolvedPicker]);

    return {
        form,
        rules,
        answers: effective,
        errors,
        branchPicker:
            resolvedPicker && resolvedPicker.branchOptions.length > 1
                ? resolvedPicker
                : null,
        visitorCountry,
        predicted,
        setEnquiryTypeId: (typeId) => {
            clearError('enquiryType');
            update({ typeId });
        },
        setEnquirerType: (enquirerType) => {
            clearError('enquirerType');
            update({ enquirerType });
        },
        setProjectKind: (projectKind) => {
            clearError('projectKind');
            update({ projectKind });
        },
        setFieldAnswer: (key, value) => {
            setErrors((current) => {
                if (!(key in current.fields)) return current;
                const { [key]: _removed, ...fields } = current.fields;
                return { ...current, fields };
            });
            update({
                fieldAnswers: { ...effective.fieldAnswers, [key]: value },
            });
        },
        setBranchId: (next) => {
            clearError('branch');
            update({ branchId: next });
        },
        setLocation: (location) => {
            clearError('location');
            update(locationPatch(location));
        },
        setMessage: (message) => update({ message }),
        validate,
    };
}
